/*
Author: Chathuraka Mallawa Arachchi
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import trash from '@/assets/trash.svg'
import { DateRange } from '@/components/Atoms'
import InputLabel from '@/components/Atoms/InputLabel'
import { useResponseId, useUrlType } from '@/components/hooks'
import { constants } from '@/constants'
import { RootState } from '@/reducers/store'
import { useAddNewField, useValidate } from '@/state/survey/formSlice'
import {
    BasicAnswerSave,
    useUpdateAnswersSubmit,
} from '@/state/survey/saveAnswerSlice'
import { Utility, Validate } from '@/utils'
import moment from 'moment'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { ValidationManager } from '../../SurveyModel'
import nodeGenerator from '../../SurveyModel/generateNodes'
import poaAnswerDetails from '../../SurveyModel/poaQuestions/poaAnswerDetails'
import { DateRangeType } from '../../SurveyModel/types/commonTypes'

interface IdateItem {
    name: string
    index: number
    value: DateRangeType | null
}

const DateRangeMatrixNode = (props: any) => {
    const { node, disabled, height } = props
    const addNewFieldValue = useAddNewField()
    //const deleteField = useDeleteField();
    const form = useSelector((state: RootState) => state.form)
    const validateInputField = useValidate()
    const { isPatient } = useUrlType()
    const updateAnswersDispatch = useUpdateAnswersSubmit()
    const { answerResponseId } = useResponseId()
    const [rows, setRows] = useState<IdateItem[]>([])
    const [deleteR, setDeleteR] = useState(false)
    const isInitialRun = useRef(true)
    const lastRowRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!isPatient && deleteR && answerResponseId) {
            const answers: BasicAnswerSave[] =
                poaAnswerDetails.generateQuestionAnswerSubmitFormat(form, node)
            updateAnswersDispatch(answers, answerResponseId)
            setDeleteR(() => false)
            isInitialRun.current = true
        }
    }, [deleteR, answerResponseId])

    useEffect(() => {
        if (isInitialRun.current && node) {
            isInitialRun.current = false
            const localRows: IdateItem[] = []
            if (typeof form[node.name]?.value === 'string') {
                const nodeValue = form[node.name]?.value
                    ? JSON.parse(form[node.name].value as string) // Safely cast to string
                    : []
                if (Array.isArray(nodeValue) && nodeValue.length > 0) {
                    nodeValue.map((IdateItem: DateRangeType, index: number) => {
                        const rowItem: IdateItem = {
                            name: `${node.name + index}`,
                            index: index,
                            value: IdateItem,
                        }
                        const fieldNode = nodeGenerator.generateNode(
                            rowItem.name,
                            constants.DateRange
                        )
                        const validatedForm =
                            ValidationManager.applyCommonValidationsWithValue(
                                fieldNode,
                                rowItem.value
                            )
                        addNewFieldValue(rowItem.name, validatedForm)
                        localRows.push(rowItem)
                    })
                    setRows(localRows)
                } else {
                    addNewRow()
                }
            }
        }
    }, [node, isInitialRun, addNewFieldValue, form])

    function sortByStartDate(dateRanges: DateRangeType[]): DateRangeType[] {
        return dateRanges.sort(
            (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
        )
    }

    // Wrap handleChange with useCallback
    const handleChange = useCallback(
        (value: DateRangeType | null, name: string) => {
            if (value) {
                value.start = moment(value.start, 'DD-MM-YYYY').format(
                    'YYYY-MM-DD'
                )
                value.end = moment(value.end, 'DD-MM-YYYY').format('YYYY-MM-DD')
            }

            validateInputField(name, value, true)
            /*the above line should update the form to process the following loop 
            : Dev testing needed.*/
            const dateRangeValues: DateRangeType[] = []
            rows.forEach((field) => {
                const range =
                    field.name === name
                        ? value
                        : ((form[field.name]?.value as DateRangeType) ?? null)

                if (range) {
                    dateRangeValues.push(range)
                }
            })
            const sortedDates = sortByStartDate(dateRangeValues)
            validateInputField(node.name, JSON.stringify(sortedDates), true)
            if (node.validationDependants) {
                node.validationDependants.forEach((element: string) => {
                    validateInputField(element, form[element].value, true)
                })
            }
        },
        [validateInputField, form, node, rows]
    )

    const addNewRow = () => {
        const uniqueId = uuidv4() // Generate a unique ID
        let available = false
        if (rows.length) {
            const lastItem = rows[rows.length - 1]
            if (!form[lastItem.name ? lastItem.name : '']?.value)
                available = true
        }
        if (!available) {
            const rowItem: IdateItem = {
                name: `${node.name}-${uniqueId}`,
                index: rows.length,
                value: null,
            }
            const fieldNode = nodeGenerator.generateNode(
                rowItem.name,
                constants.DateRange
            )
            const validatedForm =
                ValidationManager.applyCommonValidationsWithValue(fieldNode, '')
            addNewFieldValue(rowItem.name, validatedForm)
            setRows([...rows, rowItem])
        }

        if (isPatient) {
            setTimeout(() => {
                window.scrollTo({
                    top: document.documentElement.scrollHeight,
                    behavior: 'smooth',
                })
            }, 100)
        }
    }

    const removeAnswer = (name: string) => {
        const filteredRows = rows.filter((rowItem) => rowItem.name !== name)
        const dateRangeValues: DateRangeType[] = filteredRows
            .map((field) => form[field.name]?.value as DateRangeType)
            .filter(Boolean)

        validateInputField(node.name, JSON.stringify(dateRangeValues), true)
        setRows(filteredRows)
        setDeleteR(true)
    }

    const handleOnBlur = () => {
        if (!isPatient && answerResponseId) {
            const answers: BasicAnswerSave[] =
                poaAnswerDetails.generateQuestionAnswerSubmitFormat(form, node)
            updateAnswersDispatch(answers, answerResponseId)
        }
    }

    const renderComponents = (rowElement: IdateItem, index: number) => {
        return (
            <React.Fragment key={rowElement.name}>
                <div
                    ref={index === rows.length - 1 ? lastRowRef : null}
                    className={`date-range-block ${isPatient ? '' : 'flex'}${index !== 0 ? ' rest' : ''}`}
                >
                    <DateRange
                        propClasses=""
                        isPatient={isPatient}
                        onBlur={handleOnBlur}
                        initialDate={
                            (form[rowElement.name]?.value as DateRangeType) ??
                            null
                        }
                        onChange={handleChange}
                        name={rowElement.name}
                        isInValid={Validate.isInvalid(form[rowElement.name])}
                        disabled={disabled}
                    />
                    {isPatient && form[rowElement.name]?.value ? (
                        <div className="remove-link flex items-center justify-center">
                            <button
                                type="button"
                                onClick={() => removeAnswer(rowElement.name)}
                            >{`${node.removeLabel}`}</button>
                        </div>
                    ) : (
                        <div className="flex-1 items-center trash-icon">
                            {form[rowElement.name]?.value ? (
                                <img
                                    onClick={() =>
                                        removeAnswer(rowElement.name)
                                    }
                                    style={{
                                        display: disabled ? 'none' : 'block',
                                    }}
                                    src={trash}
                                />
                            ) : (
                                ''
                            )}
                        </div>
                    )}
                </div>
            </React.Fragment>
        )
    }

    const getButtonClassName = (form: any, rows: IdateItem[]): string => {
        const lastNodeName = rows.at(-1)?.name
        const value = lastNodeName ? form?.[lastNodeName]?.value : null
        return value?.start || value?.end ? 'add-link-active' : 'add-link'
    }

    return (
        <div
            style={{ height: `${height}px` }}
            className={`date-range-matrix-wrap ${Utility.getClassForWidthPx(node)}  ${Utility.getClassForWidth(node)} user-input date-range ${!isPatient ? 'poa-input' : ''} `}
        >
            {node.title ? (
                <InputLabel
                    type="title"
                    label={node.title}
                    labelClasses="input-label title-style"
                ></InputLabel>
            ) : (
                ''
            )}
            {node.subTitle ? (
                <InputLabel
                    type="subTitle"
                    label={node.subTitle}
                    labelClasses="input-label"
                ></InputLabel>
            ) : (
                ''
            )}
            {rows.map((item: IdateItem, index) =>
                renderComponents(item, index)
            )}
            {rows.length < constants.maxDateRanges ? (
                <React.Fragment>
                    {isPatient ? (
                        <div
                            className={`${getButtonClassName(form, rows)} add-link flex items-center justify-center`}
                        >
                            <button
                                className=""
                                type="button"
                                onClick={() => addNewRow()}
                            >
                                {node.addLabel}
                            </button>
                        </div>
                    ) : (
                        <div
                            style={{ display: disabled ? 'none' : 'block' }}
                            className="add-link-poa flex"
                        >
                            <button
                                className=""
                                type="button"
                                onClick={() => addNewRow()}
                            >
                                {node.addLabel}
                            </button>
                        </div>
                    )}
                </React.Fragment>
            ) : (
                ''
            )}
        </div>
    )
}

export default DateRangeMatrixNode
